<template lang="pug">
  div
    b-loading(:active.sync="conversionInProgress")
    h2.has-text-weight-bold {{ $t('Convert the Dataset Excel Template into an ecoSpold2 file') }}
    p.has-bottom-margin
      | {{ $t('In case you have created an accompanying User Master Data file, please queue it too for uploading.') }}
      |
      | {{ $t('After uploading the file(s), the Converter will validate the Dataset(s) and generate an ecoSpold2 file if no errors are reported.') }}
      |
      | {{ $t('You may then, subsequently submit your new ecoSpold2 file to ecoinvent for review.') }}
      |
      span.is-italic {{ $t('Please note that the submission step is not within scope of the Converter.') }}
      |
      | {{ $t('Should there be errors detected during the validation process, they will be sent to the "Error Log" for your immediate attention.') }}
    .columns
      .column
        b-field.file.has-addons
          b-upload(v-model="files" :accept="mimeTypes" :disabled="fileUploadButtonDisabled" multiple)
            a.button(:disabled="fileUploadButtonDisabled")
              b-icon(icon="upload")
              span {{ $t('Add Dataset') }}
          b-upload(v-model="umd" :accept="mimeTypes" :disabled="umdUploadButtonDisabled")
            a.button(:disabled="umdUploadButtonDisabled")
              span {{ $t('Add User Masterdata') }}
      .column.has-text-centered-tablet
        .block
          .field
            b-checkbox(v-model="blame" false-value="BLAME_DATASET" true-value="BLAME_UMD")
              b-tooltip(
              :label="$t('Either no UMD will be created and all errors are reported in the dataset or UMD will be created automatically, resulting in less errors. However, you will have to complete the UMD in a second upload.')"
              position="is-right"
              animated
              multilined)
                | {{ $t('Allow automatic User Master Data generation') }}
        a.button.is-green(v-if="showStart" :disabled="startDisabled"
        :class="{ 'is-loading': conversionInProgress }" @click="startConversion")
          b-icon(icon="chevron-circle-right")
          span {{ $t('Start Conversion') }}
        a.button.is-green(v-if="showResultDownload" :href="resultUrl" :download="resultName")
          b-icon(icon="download")
          span {{ $t('Download EcoSpold2') }}
        a.button.is-outlined.is-danger(v-if="showTemplateDownload" :href="templateUrl" :download="templateName")
          b-icon(icon="download")
          span {{ $t('Template with validation log') }}
        a.button.is-outlined.is-danger(v-if="showUMDDownload" :href="umdUrl" :download="umdName")
          b-icon(icon="download")
          span {{ $t('UMD with validation log') }}
      .column.has-text-right-tablet
        a.button.is-danger(:disabled="restartDisabled" @click="restart")
          b-icon(icon="undo")
          span {{ $t('Restart') }}
    div(v-if="showResultDownload")
      div.add-top-margin.message
        .message-body
          h3.has-text-weight-bold {{ $t('The datasets were successfully converted to the ecospold2 format.') }}
    div(v-if="hasErrors")
      error-statistics(:errors="validationErrors")
    .has-bottom-margin
    div(v-if="hasUploadedFiles")
      h3.has-text-weight-bold {{ $t('Template to Convert') }}
      ul
        li(v-for="file in files") {{ file.name }}, {{ fileSize(file.size) }} kB, {{ fileDate(file.lastModified) }}
      p
    div(v-if="hasUploadedMasterData")
      h3.has-text-weight-bold {{ $t('User Masterdata') }}
      ul
        li(v-for="file in umd") {{ file.name }}, {{ fileSize(file.size) }} kB, {{ fileDate(file.lastModified) }}
      p
</template>

<script>
import { CONTENT_TYPE_XLSX, getTemplateConversionTime, uploadTemplate } from '../utils/api'
import ConvertDownloadButton from './ConvertDownloadButton'
import ErrorStatistics from './ErrorStatistics'

const CONVERSION_NEW = 0
const CONVERSION_IN_PROGRESS = 1
const CONVERSION_SUCCESS = 2
const CONVERSION_SUCCESS_TEMPLATE = 2
const CONVERSION_SUCCESS_TEMPLATE_UMD = 4
const CONVERSION_FAILED = 5
const CONVERSION_FAILED_TEMPLATE = 6
const CONVERSION_FAILED_TEMPLATE_UMD = 7
const CONVERSION_RESET = 8

export default {
  name: 'TemplateUpload',
  components: {
    ErrorStatistics,
    ConvertDownloadButton
  },
  data() {
    return {
      mimeTypes: CONTENT_TYPE_XLSX,
      maxUploads: process.env.ECOINVENT_UPLOAD_MAX_XLSX_FILES,
      blame: 'BLAME_UMD',
      files: [],
      umd: [],
      conversionState: CONVERSION_NEW,
      resultUrl: '',
      resultName: '',
      templateUrl: '',
      templateName: '',
      umdUrl: '',
      umdName: '',
      validationErrors: {}
    }
  },
  watch: {
    files(newArray) {
      if (newArray.length > this.maxUploads) {
        this.files = []
        this.$dialog.alert({
          title: 'Error',
          message: this.$t('You can upload at most') + ' ' + this.maxUploads + ' ' + this.$t('file') + '<br>' +
            this.$t('Please try again with less files.'),
          type: 'is-danger',
          hasIcon: true,
          icon: 'times-circle'
        })
      }
    }
  },
  computed: {
    hasErrors() {
      return Object.keys(this.validationErrors).length > 0
    },
    showStart() {
      return this.conversionState === CONVERSION_NEW ||
        this.conversionState === CONVERSION_RESET ||
        this.conversionState === CONVERSION_IN_PROGRESS
    },
    startDisabled() {
      return this.conversionState === CONVERSION_IN_PROGRESS ||
        this.uploadedFiles > this.maxUploads ||
        this.uploadedFiles === 0
    },
    showUMDDownload() {
      return (this.conversionState === CONVERSION_SUCCESS_TEMPLATE_UMD ||
        this.conversionState === CONVERSION_FAILED_TEMPLATE_UMD) && this.umdUrl !== ''
    },
    showTemplateDownload() {
      return (this.showUMDDownload || this.conversionState === CONVERSION_SUCCESS_TEMPLATE ||
        this.conversionState === CONVERSION_FAILED_TEMPLATE) && this.templateUrl !== ''
    },
    showResultDownload() {
      return this.conversionState === CONVERSION_SUCCESS ||
        this.conversionState === CONVERSION_SUCCESS_TEMPLATE ||
        this.conversionState === CONVERSION_SUCCESS_TEMPLATE_UMD ||
        (this.conversionState === CONVERSION_FAILED_TEMPLATE && this.resultUrl && this.resultName) ||
        (this.conversionState === CONVERSION_FAILED_TEMPLATE_UMD && this.resultUrl && this.resultName)
    },
    conversionDone() {
      return this.conversionState === CONVERSION_SUCCESS || this.conversionState === CONVERSION_SUCCESS_TEMPLATE ||
      this.conversionState === CONVERSION_SUCCESS_TEMPLATE ||
      this.conversionState === CONVERSION_SUCCESS_TEMPLATE_UMD || this.conversionState === CONVERSION_FAILED ||
      this.conversionState === CONVERSION_FAILED_TEMPLATE || this.conversionState === CONVERSION_FAILED_TEMPLATE_UMD
    },
    restartDisabled() {
      return (this.uploadedFiles === 0 && !this.uploadedMasterData) || this.conversionState === CONVERSION_IN_PROGRESS
    },
    conversionInProgress() {
      return this.conversionState === CONVERSION_IN_PROGRESS
    },
    fileUploadButtonDisabled() {
      return this.uploadedFiles >= this.maxUploads || this.conversionInProgress || this.conversionDone
    },
    umdUploadButtonDisabled() {
      return this.hasUploadedMasterData || this.conversionInProgress || this.conversionDone
    },
    uploadedFiles() {
      return this.files.length
    },
    uploadedMasterData() {
      return this.umd.length
    },
    hasUploadedFiles() {
      return !!this.uploadedFiles
    },
    hasUploadedMasterData() {
      return !!this.uploadedMasterData
    }
  },
  methods: {
    restart() {
      if (!this.restartDisabled) {
        this.conversionState = CONVERSION_RESET
        this.validationErrors = {}
        this.clearUploads()
      }
    },
    clearUploads() {
      if (!this.conversionInProgress) {
        this.files = []
        this.umd = []
      }
    },
    fileSize(bytes) {
      return Number.parseInt(bytes / 1024)
    },
    fileDate(seconds) {
      return new Date(seconds).toLocaleString('en-US')
    },
    extractTemplateUmdAndErrors(responseData) {
      if (responseData.template_file) {
        this.templateUrl = responseData.template_file
        this.templateName = this.templateUrl.split('/').pop() || 'umd.xlsx'
      } else {
        this.templateUrl = ''
        this.templateName = ''
      }
      if (responseData.umd_file) {
        this.umdUrl = responseData.umd_file
        this.umdName = this.umdUrl.split('/').pop() || 'umd.xlsx'
      } else {
        this.umdUrl = ''
        this.umdName = ''
      }
      if (responseData.errors) {
        this.validationErrors = responseData.errors
      }
    },
    startConversion() {
      if (this.hasUploadedFiles) {
        this.resultUrl = ''
        this.conversionState = CONVERSION_IN_PROGRESS
        getTemplateConversionTime()
          .then((response) => {
            let expectedTime = Number(response.data.conversion_time__avg * 1000)
            expectedTime = expectedTime.toFixed()
            this.$Progress.start(expectedTime)
          })
        uploadTemplate(this.files, this.umd, this.blame)
          .then((response) => {
            this.$Progress.finish()
            this.conversionState = CONVERSION_SUCCESS
            this.resultUrl = response.data.result_file
            this.resultName = this.resultUrl.split('/').pop() || 'template.xlsx'
            this.extractTemplateUmdAndErrors(response.data)
            this.$emit('convertTemplate2SpoldFinished', response)
            if (this.umdUrl) {
              this.conversionState = CONVERSION_FAILED_TEMPLATE_UMD
            }
          })
          .catch((error) => {
            this.$Progress.fail()
            if (error.response.status === 401) {
              this.$router.push('/login')
            } else if (error.response.status === 400) {
              this.extractTemplateUmdAndErrors(error.response.data)
              if (error.response.data.result_file) {
                this.resultUrl = error.response.data.result_file
                this.resultName = this.resultUrl.split('/').pop() || 'template.xlsx'
              }
              this.validationErrors = error.response.data.errors
              if (this.templateUrl) {
                this.conversionState = CONVERSION_FAILED_TEMPLATE
                if (this.umdUrl) {
                  this.conversionState = CONVERSION_FAILED_TEMPLATE_UMD
                }
              }
            } else {
              this.$dialog.alert({
                title: 'Error - Unexpected',
                message: this.$t('An unexpected error occurred converting your data.<br>' +
                  'Please contact us at support@ecoinvent.org.'),
                type: 'is-danger'
              })
              this.conversionState = CONVERSION_FAILED
            }
          })
        this.$emit('convertSpold2Template', this.files)
      }
    }
  }
}
</script>

<style scoped>
  .has-bottom-margin {
    margin-bottom: 2rem;
  }
  a.button {
    margin-bottom: 0.5rem;
    margin-right: 0.5rem;
  }
  .add-top-margin {
    margin-top: 1rem;
  }
</style>



// WEBPACK FOOTER //
// src/components/TemplateUpload.vue